import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Paper,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCheck,
  IconDownload,
  IconPrinter,
  IconSend,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { formatMoney } from '@erp/shared';
import { ApiError } from '../api/client';
import { unitsOfMeasureApi, vatRatesApi } from '../api/catalogs';
import {
  EDO_STATUS_COLORS,
  EDO_STATUS_LABELS,
  updsApi,
  type EdoDocflow,
  type UpdFunction,
  type UpdInput,
} from '../api/upd';
import { nomenclatureApi } from '../api/nomenclature';
import { UpdLinesEditor, emptyUpdLineRow, type UpdLineRow } from '../components/UpdLinesEditor';
import { useAuth } from '../auth/AuthContext';

const FUNCTION_LABELS: Record<UpdFunction, string> = {
  SCHFDOP: 'Счёт-фактура и передаточный документ (статус 1)',
  SCHF: 'Только счёт-фактура',
  DOP: 'Только передаточный документ, без НДС (статус 2)',
  SCHFDOPGR: 'Счёт-фактура и передаточный документ, со сведениями о перевозке',
};

const EDO_TERMINAL_STATUSES = new Set(['SIGNED', 'REJECTED', 'REVOKED', 'ERROR']);

function formatDateTime(value: string | null): string {
  return value ? new Date(value).toLocaleString('ru-RU') : '—';
}

function toDateInput(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

export function UpdFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();
  const canEditGlobally = hasRole('ADMIN', 'ACCOUNTANT', 'MANAGER');

  const [date, setDate] = useState('');
  const [transferDate, setTransferDate] = useState('');
  const [transferBasis, setTransferBasis] = useState('');
  const [shipperIsSeller, setShipperIsSeller] = useState(true);
  const [shipperName, setShipperName] = useState('');
  const [shipperAddress, setShipperAddress] = useState('');
  const [consigneeIsBuyer, setConsigneeIsBuyer] = useState(true);
  const [consigneeName, setConsigneeName] = useState('');
  const [consigneeAddress, setConsigneeAddress] = useState('');
  const [signerPosition, setSignerPosition] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signerBasis, setSignerBasis] = useState('');
  const [comment, setComment] = useState('');
  const [lines, setLines] = useState<UpdLineRow[]>([emptyUpdLineRow()]);
  const [formError, setFormError] = useState<string[] | null>(null);

  const loadedFor = useRef<string | null>(null);

  const updQuery = useQuery({
    queryKey: ['upd', id],
    queryFn: () => updsApi.get(id!),
    enabled: !!id,
  });

  const units = useQuery({ queryKey: ['units-of-measure'], queryFn: () => unitsOfMeasureApi.list() });
  const vatRates = useQuery({ queryKey: ['vat-rates'], queryFn: () => vatRatesApi.list() });
  const nomenclature = useQuery({
    queryKey: ['nomenclature', 'all'],
    queryFn: () => nomenclatureApi.list({ pageSize: 500 }),
  });

  const upd = updQuery.data?.upd ?? null;
  const canEditDocument = canEditGlobally && upd?.edoStatus === 'NOT_SENT';
  const canSendEdo = hasRole('ADMIN', 'ACCOUNTANT');

  const edoStatusQuery = useQuery({
    queryKey: ['upd', id, 'edo-status'],
    queryFn: () => updsApi.getEdoStatus(id!),
    enabled: !!id && !!upd && upd.edoStatus !== 'NOT_SENT',
    refetchInterval: (query) => {
      const status = query.state.data?.docflow?.status;
      return status && !EDO_TERMINAL_STATUSES.has(status) ? 3000 : false;
    },
  });
  const docflow: EdoDocflow | null = edoStatusQuery.data?.docflow ?? null;

  // Заполняем форму данными документа один раз при загрузке — иначе фоновый
  // рефетч перезаписывал бы то, что пользователь уже успел набрать.
  useEffect(() => {
    if (!upd || loadedFor.current === upd.id) return;
    loadedFor.current = upd.id;

    setDate(toDateInput(upd.date));
    setTransferDate(toDateInput(upd.transferDate));
    setTransferBasis(upd.transferBasis ?? '');
    setShipperIsSeller(upd.shipperIsSeller);
    setShipperName(upd.shipperName ?? '');
    setShipperAddress(upd.shipperAddress ?? '');
    setConsigneeIsBuyer(upd.consigneeIsBuyer);
    setConsigneeName(upd.consigneeName ?? '');
    setConsigneeAddress(upd.consigneeAddress ?? '');
    setSignerPosition(upd.signerPosition ?? '');
    setSignerName(upd.signerName ?? '');
    setSignerBasis(upd.signerBasis ?? '');
    setComment(upd.comment ?? '');
    setLines(
      upd.lines.map((l) => ({
        key: l.id,
        nomenclatureId: l.nomenclatureId,
        name: l.name,
        article: l.article,
        unitId: l.unitId ?? '',
        quantity: l.quantity,
        price: l.price,
        vatRateId: vatRates.data?.items.find((r) => r.code === l.vatRateCode)?.id ?? '',
      })),
    );
  }, [upd, vatRates.data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: UpdInput = {
        date,
        transferDate: transferDate || null,
        transferBasis: transferBasis || null,
        shipperIsSeller,
        shipperName: shipperIsSeller ? null : shipperName || null,
        shipperAddress: shipperIsSeller ? null : shipperAddress || null,
        consigneeIsBuyer,
        consigneeName: consigneeIsBuyer ? null : consigneeName || null,
        consigneeAddress: consigneeIsBuyer ? null : consigneeAddress || null,
        signerPosition: signerPosition || null,
        signerName: signerName || null,
        signerBasis: signerBasis || null,
        comment: comment || null,
        lines: lines.map((l) => ({
          nomenclatureId: l.nomenclatureId,
          name: l.name.trim(),
          article: l.article || null,
          unitId: l.unitId,
          quantity: l.quantity,
          price: l.price,
          vatRateId: l.vatRateId,
        })),
      };
      return updsApi.update(id!, payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['upd', id] });
      void queryClient.invalidateQueries({ queryKey: ['upds'] });
      notifications.show({ color: 'green', message: 'УПД сохранён' });
    },
    onError: (err) => {
      setFormError(err instanceof ApiError ? (err.details ?? [err.message]) : ['Не удалось сохранить']);
    },
  });

  function handleSubmit() {
    setFormError(null);
    const errors: string[] = [];
    if (!date) errors.push('Укажите дату');
    if (lines.length === 0) errors.push('Добавьте хотя бы одну строку');
    lines.forEach((l, i) => {
      if (!l.name.trim()) errors.push(`Строка ${i + 1}: укажите наименование`);
      if (!l.unitId) errors.push(`Строка ${i + 1}: укажите единицу измерения`);
      if (!l.vatRateId) errors.push(`Строка ${i + 1}: укажите ставку НДС`);
      if (!l.quantity.trim()) errors.push(`Строка ${i + 1}: укажите количество`);
      if (!l.price.trim()) errors.push(`Строка ${i + 1}: укажите цену`);
    });
    if (errors.length > 0) {
      setFormError(errors);
      return;
    }
    saveMutation.mutate();
  }

  const removeMutation = useMutation({
    mutationFn: () => updsApi.remove(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['upds'] });
      notifications.show({ color: 'green', message: 'УПД удалён' });
      navigate('/upd');
    },
    onError: (err) => {
      notifications.show({ color: 'red', message: err instanceof ApiError ? err.message : 'Не удалось удалить УПД' });
    },
  });

  function handleRemove() {
    if (!upd) return;
    if (!window.confirm(`Удалить УПД № ${upd.number}? Действие необратимо.`)) return;
    removeMutation.mutate();
  }

  const sendEdoMutation = useMutation({
    mutationFn: () => updsApi.sendEdo(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['upd', id] });
      void queryClient.invalidateQueries({ queryKey: ['upds'] });
      notifications.show({ color: 'green', message: 'УПД отправлен по ЭДО' });
    },
    onError: (err) => {
      notifications.show({ color: 'red', message: err instanceof ApiError ? err.message : 'Не удалось отправить по ЭДО' });
    },
  });

  const revokeEdoMutation = useMutation({
    mutationFn: () => updsApi.revokeEdo(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['upd', id] });
      void queryClient.invalidateQueries({ queryKey: ['upds'] });
      notifications.show({ color: 'green', message: 'Документооборот аннулирован' });
    },
    onError: (err) => {
      notifications.show({ color: 'red', message: err instanceof ApiError ? err.message : 'Не удалось аннулировать' });
    },
  });

  function handleSendEdo() {
    if (!upd) return;
    if (!upd.signerName || !upd.signerPosition) {
      notifications.show({ color: 'red', message: 'Укажите подписанта документа перед отправкой' });
      return;
    }
    sendEdoMutation.mutate();
  }

  function handleRevokeEdo() {
    if (!window.confirm('Аннулировать документооборот?')) return;
    revokeEdoMutation.mutate();
  }

  if (!upd) {
    return <Text c="dimmed">Загрузка…</Text>;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="sm">
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/upd')}>
            К списку
          </Button>
          <Title order={2}>УПД № {upd.number}</Title>
          <Badge color={EDO_STATUS_COLORS[upd.edoStatus]} variant="light">
            {EDO_STATUS_LABELS[upd.edoStatus]}
          </Badge>
        </Group>

        <Group gap="xs">
          <Button
            variant="default"
            leftSection={<IconPrinter size={16} />}
            component="a"
            href={`/upd/${upd.id}/print`}
            target="_blank"
          >
            Печать
          </Button>
          <Button
            variant="default"
            leftSection={<IconDownload size={16} />}
            onClick={() => window.open(`/upd/${upd.id}/print?download=1`, '_blank')}
          >
            Скачать PDF
          </Button>
          {canSendEdo && upd.edoStatus === 'NOT_SENT' && (
            <Button
              leftSection={<IconSend size={16} />}
              onClick={handleSendEdo}
              loading={sendEdoMutation.isPending}
            >
              Отправить по ЭДО
            </Button>
          )}
          {canSendEdo && docflow && !EDO_TERMINAL_STATUSES.has(docflow.status) && (
            <Button
              color="red"
              variant="light"
              leftSection={<IconX size={16} />}
              onClick={handleRevokeEdo}
              loading={revokeEdoMutation.isPending}
            >
              Аннулировать
            </Button>
          )}
          {canEditDocument && (
            <Button
              color="red"
              variant="light"
              leftSection={<IconTrash size={16} />}
              onClick={handleRemove}
              loading={removeMutation.isPending}
            >
              Удалить
            </Button>
          )}
        </Group>
      </Group>

      <Text size="sm" c="dimmed">
        {upd.organization.shortName} → {upd.counterparty.shortName}
        {upd.invoice && <> · на основании счёта № {upd.invoice.number}</>}
        {' · '}
        {FUNCTION_LABELS[upd.function]}
      </Text>

      {formError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          <Stack gap={2}>
            {formError.map((e, i) => (
              <Text key={i} size="sm">
                {e}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      <Group grow align="flex-start">
        <TextInput
          label="Дата документа"
          type="date"
          value={date}
          onChange={(e) => setDate(e.currentTarget.value)}
          disabled={!canEditDocument}
          required
        />
        <TextInput
          label="Дата отгрузки, передачи"
          type="date"
          value={transferDate}
          onChange={(e) => setTransferDate(e.currentTarget.value)}
          disabled={!canEditDocument}
        />
        <TextInput
          label="Основание передачи"
          placeholder="Договор, доверенность и др."
          value={transferBasis}
          onChange={(e) => setTransferBasis(e.currentTarget.value)}
          disabled={!canEditDocument}
        />
      </Group>

      <Group grow align="flex-start">
        <Stack gap={4}>
          <Checkbox
            label="Грузоотправитель совпадает с продавцом"
            checked={shipperIsSeller}
            onChange={(e) => setShipperIsSeller(e.currentTarget.checked)}
            disabled={!canEditDocument}
          />
          {!shipperIsSeller && (
            <>
              <TextInput
                placeholder="Наименование грузоотправителя"
                value={shipperName}
                onChange={(e) => setShipperName(e.currentTarget.value)}
                disabled={!canEditDocument}
              />
              <TextInput
                placeholder="Адрес грузоотправителя"
                value={shipperAddress}
                onChange={(e) => setShipperAddress(e.currentTarget.value)}
                disabled={!canEditDocument}
              />
            </>
          )}
        </Stack>
        <Stack gap={4}>
          <Checkbox
            label="Грузополучатель совпадает с покупателем"
            checked={consigneeIsBuyer}
            onChange={(e) => setConsigneeIsBuyer(e.currentTarget.checked)}
            disabled={!canEditDocument}
          />
          {!consigneeIsBuyer && (
            <>
              <TextInput
                placeholder="Наименование грузополучателя"
                value={consigneeName}
                onChange={(e) => setConsigneeName(e.currentTarget.value)}
                disabled={!canEditDocument}
              />
              <TextInput
                placeholder="Адрес грузополучателя"
                value={consigneeAddress}
                onChange={(e) => setConsigneeAddress(e.currentTarget.value)}
                disabled={!canEditDocument}
              />
            </>
          )}
        </Stack>
      </Group>

      <UpdLinesEditor
        lines={lines}
        onChange={setLines}
        canEdit={!!canEditDocument}
        nomenclatureOptions={nomenclature.data?.items ?? []}
        units={units.data?.items ?? []}
        vatRates={vatRates.data?.items ?? []}
      />

      <Text size="sm" c="dimmed">
        Всего: {formatMoney(upd.total)}
      </Text>

      {docflow && (
        <Paper withBorder p="md" radius="md">
          <Stack gap={4}>
            <Group gap="xs">
              <Text fw={500} size="sm">
                Документооборот
              </Text>
              <Badge color={EDO_STATUS_COLORS[docflow.status]} variant="light">
                {EDO_STATUS_LABELS[docflow.status]}
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              Оператор: {docflow.provider}
            </Text>
            <Text size="sm" c="dimmed">
              Отправлен: {formatDateTime(docflow.sentAt)} · Доставлен: {formatDateTime(docflow.deliveredAt)} ·
              Подписан: {formatDateTime(docflow.signedAt)}
            </Text>
            {docflow.status === 'REJECTED' && docflow.rejectReason && (
              <Text size="sm" c="red">
                Причина отказа: {docflow.rejectReason}
              </Text>
            )}
            {docflow.status === 'ERROR' && docflow.lastError && (
              <Text size="sm" c="red">
                Ошибка: {docflow.lastError}
              </Text>
            )}
          </Stack>
        </Paper>
      )}

      <Group grow align="flex-start">
        <TextInput
          label="Подписант — должность"
          value={signerPosition}
          onChange={(e) => setSignerPosition(e.currentTarget.value)}
          disabled={!canEditDocument}
        />
        <TextInput
          label="Подписант — ФИО"
          value={signerName}
          onChange={(e) => setSignerName(e.currentTarget.value)}
          disabled={!canEditDocument}
        />
        <TextInput
          label="Основание полномочий"
          value={signerBasis}
          onChange={(e) => setSignerBasis(e.currentTarget.value)}
          disabled={!canEditDocument}
        />
      </Group>

      <Textarea
        label="Комментарий"
        value={comment}
        onChange={(e) => setComment(e.currentTarget.value)}
        disabled={!canEditDocument}
        autosize
        minRows={1}
      />

      {canEditDocument && (
        <Group>
          <Button leftSection={<IconCheck size={16} />} onClick={handleSubmit} loading={saveMutation.isPending}>
            Сохранить
          </Button>
        </Group>
      )}
    </Stack>
  );
}
